import type { DocumentTag } from '../../constants/document-tags'

import { COMMON } from './common'
import { FIELD_LABELS } from './fields'
import { FINANCE_TERMS } from './finance'

const UPLOAD_DOCUMENTS = 'Dokumente hochladen'

/** What blocks an upload, a merge or a split of filed documents. */
export const DOCUMENT_ERRORS = {
  readOnlyPdf: 'Nur ein PDF kann gelesen werden.',
  readSourceTooLarge: (maxMb: number) =>
    `Das PDF ist größer als ${maxMb} MB und kann nicht gelesen werden.`,
  readFailed: (reason: string) =>
    `Das PDF konnte nicht ausgelesen werden: ${reason}`,
  mergeNeedsTwo: 'Zum Zusammenführen werden mindestens 2 Dokumente gebraucht.',
  mergeTooMany: (max: number) =>
    `Es können höchstens ${max} Dokumente zusammengeführt werden.`,
  mergeTotalSizeExceeded: (maxMb: number) =>
    `Die Dokumente sind zusammen größer als ${maxMb} MB.`,
  mergeMimeUnsupported: (mimeType: string) =>
    `Dateityp „${mimeType}“ kann nicht zusammengeführt werden.`,
  splitNeedsBucket: 'Bitte mindestens einen Abschnitt angeben.',
  splitOnlyPdf: 'Nur PDF-Dokumente können geteilt werden.',
  splitPageOutOfRange: (page: number, total: number) =>
    `Seite ${page} gibt es nicht — das Dokument hat ${total} Seiten.`,
  splitSourceTooLarge: (maxMb: number) =>
    `Das Dokument ist größer als ${maxMb} MB und kann nicht geteilt werden.`,

  /** The upload reached S3 but could not be turned into a document. */
  uploadNotFound:
    'Der Upload wurde nicht gefunden. Bitte lade die Datei erneut hoch.',
  uploadSizeUnknown: 'Der Upload konnte nicht überprüft werden.',
  uploadKeyInvalid: 'Der Upload konnte diesem Projekt nicht zugeordnet werden.',
  uploadChanged:
    'Die Datei hat sich während des Uploads geändert. Bitte lade sie erneut hoch.',
  isInvoice: (number: string) =>
    `Das Dokument ist die Rechnung ${number} in den Finanzen und kann nicht gelöscht werden.`,
} as const

export const DOCUMENTS = {
  sharedByAdvisor: 'Von deinem Berater geteilt',
  sharedByAdvisorSubtitle:
    'Diese Unterlagen hat dein Berater für dich freigegeben',

  tagLabels: {
    EXPOSE: 'Exposé',
    LIVING_AREA_CALCULATION: 'Wohnflächenberechnung',
    FLOOR_PLAN: 'Grundriss',
    ENERGY_CERTIFICATE: 'Energieausweis',
    BUILDING_SECTIONS: 'Schnitte',
    EXPERT_REPORT: 'Gutachten',
    AIRTIGHTNESS_TEST: 'Dichtigkeitsprüfung',
    OFFER: 'Angebot',
    COST_ESTIMATE: 'Kostenschätzung',
    CONTRACT: FINANCE_TERMS.contract,
    INVOICE: FINANCE_TERMS.invoice,
    MISC: COMMON.other,
  } satisfies Record<DocumentTag, string>,

  uploadDocuments: UPLOAD_DOCUMENTS,
  dropFileHere: 'Datei hierher ziehen',
  orClickToSelect: 'oder klicken zum Auswählen',
  uploadMaxSizeHint: (max: number) => `Maximal ${max} MB`,
  selectTags: 'Typ auswählen',
  descriptionLabel: COMMON.optionalLabel(FIELD_LABELS.description.label),
  descriptionPlaceholder: 'Kurze Beschreibung…',
  uploadButton: 'Hochladen',
  pageOf: (page: number, count: number) => `Seite ${page} von ${count}`,
  previousPage: 'Vorherige Seite',
  nextPage: 'Nächste Seite',
  loadError: 'Das PDF konnte nicht geladen werden.',

  releasedToCustomer: 'Freigegeben',
  notReleasedToCustomer: 'Nicht freigegeben',
  releaseToCustomer: 'Freigeben',
  revokeRelease: 'Freigabe entfernen',
  uploadedByCustomer: 'Vom Kunden hochgeladen',
  // Set whenever a newer document supersedes this one: a merge, a split, or
  // a re-exported planning PDF. The row is kept so old links keep resolving.
  deprecatedBadge: 'Veraltet',

  // Merge / Split / Selection
  mergeButton: 'Zusammenfügen',
  mergeModalTitle: 'Dokumente zusammenfügen',
  mergeModalOrderHint:
    'Reihenfolge der Seiten festlegen (zum Verschieben ziehen)',
  mergeModalFilenameLabel: 'Dateiname',
  mergedFilename: (base: string) => `${base} (zusammengefügt).pdf`,
  splitModalTitle: 'PDF aufteilen',
  splitModalAddBucket: 'Weiteres Dokument',
  splitModalBucketHeading: (n: number) => `Dokument ${n}`,
  splitModalPagesHeading: 'Seiten',
  splitModalSubmitLabel: 'Aufteilen',
  splitModalDragHint:
    'Ziehe Seiten von links in ein Dokument. Zum Entfernen oder Verschieben einfach erneut ziehen.',
  splitModalUnassignedHint:
    'Seiten, die keinem Dokument zugewiesen sind, werden ignoriert.',
  splitModalLoadError: 'PDF konnte nicht geladen werden',
  splitModalNoPagesLoaded: 'Keine Seiten geladen',
  previewIframeTitle: 'Dokumentvorschau',
  selectionCount: (n: number) => `${n} ausgewählt`,
  selectionHiddenCount: (n: number, hidden: number) =>
    `${n} ausgewählt (${hidden} nicht sichtbar)`,
  releaseSelected: (n: number) =>
    n === 1 ? `${n} Dokument freigeben` : `${n} Dokumente freigeben`,
  releasedButUnsubscribed: (n: number) =>
    n === 1
      ? `1 Dokument freigegeben — der Kunde hat E-Mails abbestellt und wurde nicht benachrichtigt.`
      : `${n} Dokumente freigegeben — der Kunde hat E-Mails abbestellt und wurde nicht benachrichtigt.`,

  renameDocumentTitle: 'Dokument umbenennen',
  renameDocumentDescription: 'Neuer Name (ohne Dateiendung)',

  deleteDocumentTitle: (n: number) =>
    n === 1 ? `Dokument löschen` : `Dokumente löschen`,
  deleteDocumentMessage: (n: number) =>
    n === 1
      ? `Möchtest du dieses Dokument wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
      : `Möchtest du die ${n} ausgewählten Dokumente wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
  deletePartialError: (n: number) =>
    n === 1
      ? `${n} Dokument konnte nicht gelöscht werden.`
      : `${n} Dokumente konnten nicht gelöscht werden.`,

  downloadDocument: COMMON.download,

  customerViewedAt: (date: string) => `Gesehen am ${date}`,
  notYetViewed: 'Noch nicht gesehen',

  requiredDocuments: 'Benötigte Dokumente',
  requiredDocumentsSubtitle: 'Folgende Dokumente benötigen wir von dir',
  checklistCounter: (done: number, total: number) =>
    `${done} von ${total} vorhanden`,
  clickToUpload: 'Klicken oder Datei hierher ziehen',
  /** The same tile in the advisor's shadow view, where nothing may be filed. */
  notUploadedYet: 'Noch nicht hochgeladen',
  dropToUpload: 'Hier loslassen',
  optionalDocumentsHint: 'Optional · mehrere Dokumente möglich',
  addAnotherDocument: 'Weiteres Dokument hochladen',

  /** How many of the documents a project needs are still not on file. */
  openCount: (n: number) => `${n} offen`,

  /** The tab the document's thread hangs in. The customer never reads it,
   *  which is what the heading has to say before anyone writes one — what the
   *  list itself says is `COMMENTS`, because every thread says it. */
  internalComments: 'Interne Kommentare',

  searchDocuments: 'Dokumente durchsuchen…',
  tagsLabel: COMMON.type,
  uploadedAt: 'Hochgeladen am',
  uploadedBy: 'Hochgeladen von',
  fileSize: 'Dateigröße',
  documentsCount: (n: number) => (n === 1 ? `${n} Dokument` : `${n} Dokumente`),

  noDocuments: 'Noch keine Dokumente',
  noDocumentsDescription: 'Noch keine Dokumente hochgeladen.',
  noDocumentsFiltered: 'Keine Treffer',
  noDocumentsFilteredDescription:
    'Keine Dokumente passen zu den aktuellen Filtern. Setze Suche oder Typ-Filter zurück.',

  uploadTooLarge: (max: number) =>
    `Die Datei ist zu groß. Maximal ${max} MB sind erlaubt.`,
  uploadUnsupportedType:
    'Dieser Dateityp wird nicht unterstützt. Erlaubt sind PDF, Bilder und Office-Dokumente.',
  uploadFailed: 'Der Upload ist fehlgeschlagen.',
} as const
